import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Building,
  Car,
  Laptop,
  MapPin,
  Plus,
  Printer,
  Search,
  Trash2,
  Wrench,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { api, type InventoryCategory, type InventoryStatus } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'

const ICON_MAP: Record<string, typeof Car> = {
  car: Car,
  wrench: Wrench,
  laptop: Laptop,
  building: Building,
  box: Box,
}

const STATUS_META: Record<InventoryStatus, { label: string; color: string; bg: string }> = {
  available: { label: 'Verfügbar', color: 'text-green-400', bg: 'bg-green-500/15' },
  'in-use': { label: 'In Benutzung', color: 'text-blue-400', bg: 'bg-blue-500/15' },
  maintenance: { label: 'In Wartung', color: 'text-accent', bg: 'bg-accent/15' },
  defect: { label: 'Defekt', color: 'text-red-400', bg: 'bg-red-500/15' },
}

export function InventoryPage() {
  const qc = useQueryClient()
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const { data: categoriesData } = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => api.listInventoryCategories(),
  })

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ['inventory', 'items', categoryFilter, statusFilter, search],
    queryFn: () =>
      api.listInventory({
        categoryId: categoryFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      }),
  })

  const categories = categoriesData?.categories ?? []
  const items = itemsData?.items ?? []

  return (
    <div className="space-y-4">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border-subtle -mb-px">
        <button
          type="button"
          onClick={() => setCategoryFilter('')}
          className={cn(
            'px-3 py-2 text-xs border-b-2 transition-colors',
            !categoryFilter
              ? 'border-accent text-accent font-medium'
              : 'border-transparent text-text-secondary hover:text-text-primary',
          )}
        >
          Alle
        </button>
        {categories.map((cat) => {
          const Icon = ICON_MAP[cat.icon ?? ''] ?? Box
          return (
            <button
              type="button"
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 transition-colors',
                categoryFilter === cat.id
                  ? 'border-accent text-accent font-medium'
                  : 'border-transparent text-text-secondary hover:text-text-primary',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {cat.name}
            </button>
          )
        })}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-8 h-8 text-xs"
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-36 h-8 text-xs"
        >
          <option value="">Alle Status</option>
          <option value="available">Verfügbar</option>
          <option value="in-use">In Benutzung</option>
          <option value="maintenance">In Wartung</option>
          <option value="defect">Defekt</option>
        </Select>
        <span className="flex-1" />
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> Inventar
        </Button>
      </div>

      {/* Items grid */}
      {isLoading ? (
        <p className="text-sm text-text-tertiary py-8 text-center">Lade Inventar …</p>
      ) : items.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <Box className="h-12 w-12 mx-auto text-text-tertiary/40" strokeWidth={1.5} />
          <p className="text-sm text-text-tertiary">Noch keine Inventar-Objekte erfasst.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {items.map((item) => {
            const st = STATUS_META[item.status as InventoryStatus] ?? STATUS_META.available
            const Icon = ICON_MAP[item.categoryIcon ?? ''] ?? Box
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => setSelectedItemId(item.id)}
                className="text-left rounded-xl border border-border-subtle bg-bg-elevated/40 p-3 hover:border-border-default/80 transition-all touch:opacity-100"
              >
                <div className="flex items-start gap-2 mb-2">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${item.categoryColor ?? '#6B7280'}20` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: item.categoryColor ?? '#6B7280' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{item.name}</p>
                    {item.serialNumber && (
                      <p className="text-2xs text-text-tertiary font-mono truncate">
                        {item.serialNumber}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className={cn('text-2xs px-1.5 py-0.5 rounded font-medium', st.bg, st.color)}
                  >
                    {st.label}
                  </span>
                  {item.location && (
                    <span className="inline-flex items-center gap-0.5 text-2xs text-text-tertiary">
                      <MapPin className="h-2.5 w-2.5" />
                      {item.location}
                    </span>
                  )}
                </div>
                {item.responsibleUserName && (
                  <p className="text-2xs text-text-tertiary mt-1.5 truncate">
                    {item.responsibleUserName}
                  </p>
                )}
              </button>
            )
          })}
        </div>
      )}

      {selectedItemId && (
        <InventoryDetailPanel
          itemId={selectedItemId}
          categories={categories}
          onClose={() => setSelectedItemId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['inventory'] })}
        />
      )}

      {createOpen && (
        <InventoryCreatePanel
          categories={categories}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['inventory'] })
            setCreateOpen(false)
          }}
        />
      )}
    </div>
  )
}

// ── Detail Panel ────────────────────────────────────────────────────────

function InventoryDetailPanel({
  itemId,
  onClose,
  onChanged,
}: {
  itemId: string
  categories: InventoryCategory[]
  onClose: () => void
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const [maintenanceDesc, setMaintenanceDesc] = useState('')
  const [moveLocation, setMoveLocation] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'detail', itemId],
    queryFn: () => api.getInventoryItem(itemId),
  })

  const updateMut = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateInventoryItem(itemId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] })
      notify.success('Gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const maintenanceMut = useMutation({
    mutationFn: () => api.addInventoryMaintenance(itemId, { description: maintenanceDesc }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'detail', itemId] })
      setMaintenanceDesc('')
      notify.success('Wartungseintrag erstellt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const moveMut = useMutation({
    mutationFn: () => api.moveInventoryItem(itemId, moveLocation),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] })
      setMoveLocation('')
      notify.success('Standort aktualisiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: () => api.deleteInventoryItem(itemId),
    onSuccess: () => {
      onChanged()
      onClose()
      notify.success('Archiviert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (isLoading || !data) {
    return (
      <SidePanel open onClose={onClose} title="Inventar">
        <p className="text-sm text-text-tertiary py-4 text-center">Lade …</p>
      </SidePanel>
    )
  }

  const { item, maintenanceLog, locationHistory, dispositionEntries: dispoEntries } = data
  const st = STATUS_META[item.status as InventoryStatus] ?? STATUS_META.available

  return (
    <SidePanel
      open
      onClose={onClose}
      title={item.name}
      description={`${item.categoryName ?? 'Unkategorisiert'} · ${st.label}`}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="text-red-400 hover:text-red-300"
            onClick={() => {
              if (confirm('Objekt wirklich archivieren?')) deleteMut.mutate()
            }}
          >
            <Trash2 className="h-4 w-4" /> Archivieren
          </Button>
          <span className="flex-1" />
          <a
            href={api.inventoryQrUrl(itemId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-border-default text-text-secondary hover:text-text-primary transition-colors"
          >
            <Printer className="h-3.5 w-3.5" /> QR drucken
          </a>
        </div>
      }
    >
      <div className="space-y-5 pb-2">
        {/* Status */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-2xs">Status</Label>
            <Select
              value={item.status}
              onChange={(e) => updateMut.mutate({ status: e.target.value })}
              className="h-8 text-xs"
            >
              <option value="available">Verfügbar</option>
              <option value="in-use">In Benutzung</option>
              <option value="maintenance">In Wartung</option>
              <option value="defect">Defekt</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-2xs">Zustand</Label>
            <Select
              value={item.condition}
              onChange={(e) => updateMut.mutate({ condition: e.target.value })}
              className="h-8 text-xs"
            >
              <option value="good">Gut</option>
              <option value="fair">Mäßig</option>
              <option value="poor">Schlecht</option>
            </Select>
          </div>
        </div>

        {/* Info */}
        <div className="space-y-2 text-xs">
          {item.serialNumber && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Seriennummer</span>
              <span className="text-text-primary font-mono">{item.serialNumber}</span>
            </div>
          )}
          {item.qrCode && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">QR-Code</span>
              <span className="text-text-primary font-mono">{item.qrCode}</span>
            </div>
          )}
          {item.location && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Standort</span>
              <span className="text-text-primary">{item.location}</span>
            </div>
          )}
          {item.responsibleUserName && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Verantwortlich</span>
              <span className="text-text-primary">{item.responsibleUserName}</span>
            </div>
          )}
          {item.purchaseDate && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Kaufdatum</span>
              <span className="text-text-primary">{item.purchaseDate}</span>
            </div>
          )}
          {item.purchasePriceCents != null && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Kaufpreis</span>
              <span className="text-text-primary">
                {(item.purchasePriceCents / 100).toFixed(2)} EUR
              </span>
            </div>
          )}
          {item.warrantyUntil && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Garantie bis</span>
              <span className="text-text-primary">{item.warrantyUntil}</span>
            </div>
          )}
          {item.insuranceInfo && (
            <div className="flex justify-between">
              <span className="text-text-tertiary">Versicherung</span>
              <span className="text-text-primary truncate max-w-[160px]">{item.insuranceInfo}</span>
            </div>
          )}
        </div>

        {/* Move location */}
        <div className="space-y-2 border-t border-border-subtle pt-3">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            Standort ändern
          </p>
          <div className="flex gap-2">
            <Input
              value={moveLocation}
              onChange={(e) => setMoveLocation(e.target.value)}
              placeholder="Neuer Standort"
              className="h-8 text-xs flex-1"
            />
            <Button
              size="sm"
              disabled={!moveLocation.trim() || moveMut.isPending}
              onClick={() => moveMut.mutate()}
            >
              <MapPin className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Maintenance log */}
        <div className="space-y-2 border-t border-border-subtle pt-3">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            Wartungsprotokoll ({maintenanceLog.length})
          </p>
          {maintenanceLog.length > 0 && (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {maintenanceLog.map((m) => (
                <div
                  key={m.id}
                  className="text-xs p-2 rounded bg-bg-overlay/30 border border-border-subtle"
                >
                  <div className="flex justify-between">
                    <span className="text-text-primary">{m.description}</span>
                    {m.costCents != null && (
                      <span className="text-text-tertiary">
                        {(m.costCents / 100).toFixed(2)} EUR
                      </span>
                    )}
                  </div>
                  <p className="text-2xs text-text-tertiary mt-0.5">
                    {m.performedByName ?? 'Unbekannt'} &middot;{' '}
                    {new Date(m.performedAt).toLocaleDateString('de-DE')}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={maintenanceDesc}
              onChange={(e) => setMaintenanceDesc(e.target.value)}
              placeholder="Wartung beschreiben"
              className="h-8 text-xs flex-1"
            />
            <Button
              size="sm"
              disabled={!maintenanceDesc.trim() || maintenanceMut.isPending}
              onClick={() => maintenanceMut.mutate()}
            >
              <Wrench className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Location history */}
        {locationHistory.length > 0 && (
          <div className="space-y-2 border-t border-border-subtle pt-3">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Standortverlauf ({locationHistory.length})
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {locationHistory.map((l) => (
                <div key={l.id} className="text-2xs text-text-tertiary">
                  {l.fromLocation ?? '—'} &rarr; {l.toLocation} &middot;{' '}
                  {new Date(l.movedAt).toLocaleDateString('de-DE')} &middot;{' '}
                  {l.movedByName ?? 'System'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current disposition entries */}
        {dispoEntries.length > 0 && (
          <div className="space-y-2 border-t border-border-subtle pt-3">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Disposition ({dispoEntries.length})
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {dispoEntries.map((d) => (
                <div key={d.id} className="text-xs p-1.5 rounded bg-bg-overlay/30">
                  <span className="text-text-primary">{d.title}</span>
                  <span className="text-text-tertiary ml-2">
                    {new Date(d.startTime).toLocaleDateString('de-DE')} –{' '}
                    {new Date(d.endTime).toLocaleDateString('de-DE')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  )
}

// ── Create Panel ────────────────────────────────────────────────────────

function InventoryCreatePanel({
  categories,
  onClose,
  onCreated,
}: {
  categories: InventoryCategory[]
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [serialNumber, setSerialNumber] = useState('')
  const [location, setLocation] = useState('')
  const [status, setStatus] = useState<InventoryStatus>('available')

  const createMut = useMutation({
    mutationFn: () =>
      api.createInventoryItem({
        name,
        description: description || null,
        categoryId: categoryId || null,
        serialNumber: serialNumber || null,
        location: location || null,
        status,
      }),
    onSuccess: () => {
      notify.success('Inventar-Objekt erstellt')
      onCreated()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Neues Inventar-Objekt"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={!name.trim() || createMut.isPending} onClick={() => createMut.mutate()}>
            Erstellen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. ThinkPad T14s"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label>Beschreibung</Label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-none"
            placeholder="Optionale Beschreibung"
          />
        </div>
        <div className="space-y-2">
          <Label>Kategorie</Label>
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Keine</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Seriennummer</Label>
            <Input
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="SN-12345"
            />
          </div>
          <div className="space-y-2">
            <Label>Standort</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Buero 202"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onChange={(e) => setStatus(e.target.value as InventoryStatus)}>
            <option value="available">Verfügbar</option>
            <option value="in-use">In Benutzung</option>
            <option value="maintenance">In Wartung</option>
            <option value="defect">Defekt</option>
          </Select>
        </div>
      </div>
    </SidePanel>
  )
}
